import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useParams } from 'react-router-dom';
import { Typography, Stack, Grid, Box } from '@mui/material';
import { useSelector } from '../../../../redux/store';
import Lightbox from '../../../../reusable-components/lightbox';
import Iconify from '../../../../reusable-components/iconify';
import { fMonthDayTimeYear } from '../../../../utils/formatTime';
import TicketMessageItem from './TicketMessageItem';
import TicketUserAction from '../TicketUserAction';
import { getAllImagesForLighthouse, getImageID } from './newTicket/TicketHelper';
import { getMessagesAndUserActionsSorted } from '../../../../reusable-components/ChatBox/chatHelper';
import LoadingAnimation from '../../../../reusable-components/loader/LoadingAnimation';

TicketMessageList.propTypes = {
  ticketId: PropTypes.number, //this comes from embeded tickets not the main ticket page
};

export default function TicketMessageList({ ticketId }) {
  const location = useLocation();
  const { id = '' } = useParams();
  const subticketId = new URLSearchParams(location.search).get('subticket');
  const [selectedImage, setSelectedImage] = useState(-1);
  const { ticketIsLoading } = useSelector((state) => state.tickets);
  const { ticketMessages } = useSelector((state) => state.tickets.data.ticketById[ticketId || subticketId || id] || {}); 
  const imagesLightbox = getAllImagesForLighthouse(ticketMessages);

  const { allTicketUsers, subtickets, mainTicket } =
    useSelector((state) => state.tickets.data.ticketInfoById[ticketId || id] || {}) || {};
  const selectedSubticket = subticketId ? subtickets?.find((st) => st.id.toString() === subticketId) : null;

  const messagesAndUserActionsSorted = useMemo(() => {
    if (ticketMessages && (subtickets || allTicketUsers)) {
      const allUsers = subticketId
        ? subtickets?.find((st) => st.id.toString() === subticketId)?.allTicketUsers || []
        : allTicketUsers;
      return getMessagesAndUserActionsSorted(ticketMessages, allUsers, 'ticket');
    }
    return [];
  }, [ticketMessages, allTicketUsers, subticketId, subtickets]);

  const handleOpenLightbox = (imageUrl) =>
    setSelectedImage(imagesLightbox?.findIndex((image) => getImageID(image.src) === getImageID(imageUrl)) ?? -1);

  return (
    <Box>
      {ticketIsLoading && (
        <Grid container direction="row" justifyContent="center" alignItems="center" sx={{ mt: 5 }}>
          <Box
            sx={{
              zIndex: 999999999999999,
              height: '100%',
              minHeight: '10vh',
            }}
          >
            <LoadingAnimation />
          </Box>
        </Grid>
      )}

      <Box sx={{ fontSize: '13px', textAlign: 'center', ml: 2, mb: 1, mt: 2.5 }}>
        <Iconify icon="mingcute:calendar-line" width="32" height="32" sx={{ mr: 0.5 }} />
        {`Ticket was created on ${fMonthDayTimeYear(
            subticketId ? selectedSubticket?.createdOn : mainTicket?.createdOn
        )} by ${
                  subticketId ? selectedSubticket?.createdBy : mainTicket?.createdBy
        }`}
      </Box>

      {messagesAndUserActionsSorted?.map((item, idx) => {
        if (item.type === 'message') {
          if (item.systemMessage) {
            return (
              <Stack key={item.id} direction="row" justifyContent="center" sx={{ my: 3 }}>
                <Typography noWrap variant="caption" sx={{ color: 'black' }}>
                  {`${item.note},`} &nbsp;
                  {fMonthDayTimeYear(item.sentOn)}
                </Typography>
              </Stack>
            );
          }
          // Check if the current message has an attachment.
          const hasAttachment = item.file;
          const messageIndex = ticketMessages.findIndex((msg) => msg.id === item.id);
          const previousAttachmentId = ticketMessages[messageIndex - 1]?.attachFileId;
          const nextAttachmentId = ticketMessages[messageIndex + 1]?.attachFileId;
          if (hasAttachment) {
            if (item.attachFileId !== previousAttachmentId) {
              return (
                <TicketMessageItem
                  key={item.id}
                  message={item}
                  fileArray={ticketMessages.filter((msg) => msg.attachFileId === item.attachFileId && msg.file)}
                  nextMessage
                  onOpenLightbox={(imgUrl) => handleOpenLightbox(imgUrl)}
                />
              );
            }
            if (item.attachFileId === 0) {
              return (
                <TicketMessageItem
                  key={idx}
                  message={item}
                  fileArray={[item]}
                  nextMessage={ticketMessages[messageIndex + 1] || null}
                  onOpenLightbox={(imgUrl) => handleOpenLightbox(imgUrl)}
                />
              );
            }
            return null;
          }

          return (
            <TicketMessageItem
              key={item.id}
              message={item}
              nextMessage={ticketMessages[messageIndex + 1] || null}
              onOpenLightbox={(imgUrl) => handleOpenLightbox(imgUrl)}
            />
          );
        }
        if (item.type === 'addUser' || item.type === 'removeUser')
          return <TicketUserAction item={item} idx={idx} key={`${item.type}-${idx}`} />;
        return null;
      })}
      <Lightbox
        index={selectedImage}
        slides={imagesLightbox}
        open={selectedImage >= 0}
        close={() => setSelectedImage(-1)}
      />
    </Box>
  );
}
